/**
 * Product Configurator — Premium Jewelry Dynamic Media System
 * ============================================================
 * Dynamic pricing + media swap from custom.product_config metafield JSON.
 *
 * Media resolution priority:
 *   1. Combination key  (metal + shape)
 *   2. Metal only
 *   3. Shape only
 *   4. Default Shopify media (fallback)
 *
 * Reads config from #productConfigData, listens for option changes,
 * recalculates total price (variant base + config deltas), updates
 * displayed price, swaps media based on metal+shape combination,
 * and updates line item property hidden inputs.
 */
(function () {
  'use strict';

  /* ─── Selectors ────────────────────────────────────── */
  const SELECTORS = {
    configurator: '[data-product-configurator]',
    configData: '#productConfigData',
    productWrapper: '.m-main-product',
    priceWrapper: '.product-block-group--price .m-price, .m-price',
    salePrice: '.m-price-item--sale',
    regularPrice: '.m-price-item--regular',
    optionInput: '[data-config-option]',
    optionLabel: '[data-config-label]',
    engravingCheckbox: '[data-config-option="engraving"]',
    engravingInput: '[data-engraving-input]',
    // Gallery
    desktopGallery: '.custom-gallery--desktop',
    mobileGallery: '.custom-gallery--mobile',
    mainSlider: '.js-main-slider .swiper-wrapper',
    thumbSlider: '.js-thumb-slider .swiper-wrapper',
    mobileSlider: '.js-mobile-slider .swiper-wrapper',
    mobileThumbStrip: '.js-mobile-thumb-strip',
  };

  /* ─── Helpers ──────────────────────────────────────── */
  function normalizeKey(str) {
    return (str || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  }

  /* ─── Main Class ───────────────────────────────────── */
  class ProductConfigurator {
    constructor(container) {
      this.container = container;
      this.productWrapper = container.closest(SELECTORS.productWrapper);
      if (!this.productWrapper) return;

      this.productId = container.dataset.productId;
      this.configDataEl = document.querySelector(SELECTORS.configData);
      if (!this.configDataEl) return;

      try {
        this.config = JSON.parse(this.configDataEl.textContent);
      } catch (e) {
        console.error('[Configurator] Failed to parse config JSON:', e);
        return;
      }

      this.selections = {};
      this._matchedVariantId = null;
      this._matchedVariantPrice = null;
      this._lastMediaKey = null;   // cache key to skip identical re-renders
      this.mainSwiper = null;
      this.thumbSwiper = null;
      this.mobileSwiper = null;
      this.lightbox = null;

      this._init();
    }

    /* ────────────────────────────────────────────────── */
    /*  Initialization                                    */
    /* ────────────────────────────────────────────────── */
    _init() {
      this._readInitialSelections();
      this._findAndSetMatchingVariant(); 
      this._bindEvents();
      this._initPhotoSwipe();
      
      // Sync labels with current selections
      this.container.querySelectorAll('[data-config-label]').forEach(label => {
        const rawGroup = label.dataset.configLabel;
        const group = normalizeKey(rawGroup);
        const input = this.container.querySelector(`[data-config-option="${rawGroup}"]:checked`) || 
                      this.container.querySelector(`[data-config-option="${rawGroup}"]`) ||
                      this.container.querySelector(`[data-config-option="${group}"]:checked`) || 
                      this.container.querySelector(`[data-config-option="${group}"]`);
        if (input) this._updateLabel(group, input);
      });

      this.updatePrice();
      this.updateMedia();
      this._interceptFormSubmit();
    }

    /* ────────────────────────────────────────────────── */
    /*  Variant base price                                */
    /* ────────────────────────────────────────────────── */
    _getVariantBasePrice(forceDefault = false) {
      const el = this.productWrapper.querySelector('#productData[type="application/json"]');
      if (!el) return 0;
      try {
        const data = JSON.parse(el.textContent);
        this._allVariants = data.variants || [];
        
        if (forceDefault) return data.variants[0].price;

        const input = this.productWrapper.querySelector('input[name="id"]');
        const vid = input ? Number(input.value) : null;
        const v = vid ? data.variants.find((v) => v.id === vid) : data.variants[0];
        return v ? v.price : data.price || 0;
      } catch { return 0; }
    }

    /* ────────────────────────────────────────────────── */
    /*  Find & set matching Shopify variant               */
    /* ────────────────────────────────────────────────── */
    /**
     * Looks through this._allVariants for a variant whose
     * option values match the selected Center Stone + Quality.
     * If found, updates the hidden #id input so the correct
     * Shopify variant (and price) is used at checkout.
     */
    _findAndSetMatchingVariant() {
      const el = this.productWrapper.querySelector('#productData[type="application/json"]');
      if (!el) return null;
      try {
        const data = JSON.parse(el.textContent);
        this._allVariants = data.variants || [];
        const productOptions = data.options || [];

        // Build the array of selected values in the correct option order
        const selectedOptionValues = productOptions.map(optName => {
          return this._getOptionValue(optName).trim().toLowerCase();
        });

        // Find the variant that matches all selected options
        const match = this._allVariants.find(v => {
          return productOptions.every((optName, index) => {
            const selectedVal = selectedOptionValues[index];
            if (!selectedVal) return true; // if not selected or not in UI, don't filter by it
            const vVal = (v.options[index] || '').trim().toLowerCase();
            return vVal === selectedVal;
          });
        });

        if (match) {
          // Update the hidden variant input on the product form
          const idInputs = this.productWrapper.querySelectorAll('input[name="id"]');
          idInputs.forEach(input => {
            if (input.value !== String(match.id)) {
              input.value = match.id;
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });

          // Visual sync with native dropdown if it exists (e.g. in Sticky ATC)
          const nativeSelect = this.productWrapper.querySelector('.m-product-option--dropdown-select');
          if (nativeSelect) {
            nativeSelect.value = match.id;
          }

          this._matchedVariantId = match.id;
          this._matchedVariantPrice = match.price;
          return match;
        }
      } catch (e) {
        console.error('[Configurator] Failed matching variant:', e);
      }
      
      this._matchedVariantId = null;
      this._matchedVariantPrice = null;
      
      // Reset the hidden ID input to the default variant if match is lost
      const defaultId = this._getDefaultVariantId();
      if (defaultId) {
        const idInputs = this.productWrapper.querySelectorAll('input[name="id"]');
        idInputs.forEach(input => {
          if (input.value !== String(defaultId)) {
            input.value = defaultId;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }
      
      return null;
    }

    _getOptionValue(optionName) {
      let norm = normalizeKey(optionName);
      if (norm === 'stone_shape' || norm === 'center_stone_shape') {
        norm = 'shape';
      }
      if (norm === 'center_stone') {
        const selVal = this.selections['center_stone'];
        if (selVal && (/\d/.test(selVal) || selVal.toLowerCase().includes('ct'))) {
          return selVal;
        }
        if (this.selections['center_stone_type']) {
          return this.selections['center_stone_type'];
        }
      }
      if (this.selections[norm]) {
        return this.selections[norm];
      }
      const val = this._getSelectedValueForOption(optionName);
      if (val) {
        return val;
      }
      return '';
    }

    _getSelectedValueForOption(optionName) {
      let input = this.productWrapper.querySelector(`[name="options[${optionName}]"]:checked`) ||
                  this.productWrapper.querySelector(`select[name="options[${optionName}]"]`) ||
                  this.productWrapper.querySelector(`[name="${optionName}"]:checked`) ||
                  this.productWrapper.querySelector(`select[name="${optionName}"]`);
                  
      if (input) {
        return input.value;
      }
      return '';
    }

    _getDefaultVariantId() {
       const el = this.productWrapper.querySelector('#productData[type="application/json"]');
       if (!el) return null;
       try {
         const data = JSON.parse(el.textContent);
         return data.variants[0].id;
       } catch { return null; }
    }

    /* ────────────────────────────────────────────────── */
    /*  Read initially-checked options                    */
    /* ────────────────────────────────────────────────── */
    _readInitialSelections() {
      this.container.querySelectorAll(SELECTORS.optionInput).forEach((input) => {
        const group = normalizeKey(input.dataset.configOption);
        if (input.tagName === 'SELECT') {
          if (input.value) {
            this.selections[group] = input.value;
          }
        } else if ((input.type === 'radio' && input.checked) || input.type === 'checkbox') {
          this.selections[group] = input.dataset.configValue || input.value;
        }
      });

      // Fallback for Metal & Shape if they are not in configurator HTML but are in standard picker
      if (!this.selections.metal) {
        this.selections.metal = this._getSelectedValueForOption('Metal');
      }
      if (!this.selections.shape) {
        this.selections.shape = this._getSelectedValueForOption('Stone Shape') || this._getSelectedValueForOption('Shape');
      }

      // Also pick up from URL params if available
      const urlParams = new URLSearchParams(window.location.search);
      ['metal', 'shape', 'center_stone', 'carat_weight', 'quality'].forEach(key => {
        const val = urlParams.get(key);
        if (val) {
          this.selections[key] = val;
          // Also try to check the corresponding radio/select in the DOM to keep UI in sync
          const input = this.container.querySelector(`[data-config-option="${key}"][data-config-value="${val}"], [data-config-option="${key}"][value="${val}"]`) ||
                        this.productWrapper.querySelector(`input[name="Metal"][value="${val}"], input[name="options[Metal]"][value="${val}"], input[name="Stone Shape"][value="${val}"], input[name="options[Stone Shape]"][value="${val}"]`);
          if (input) {
            if (input.type === 'radio' || input.type === 'checkbox') input.checked = true;
            else input.value = val;
          }
        }
      });
    }

    /* ────────────────────────────────────────────────── */
    /*  PhotoSwipe Initialization                         */
    /* ────────────────────────────────────────────────── */
    _initPhotoSwipe() {
      if (!window.MinimogLibs || !window.MinimogLibs.PhotoSwipeLightbox) return;

      this.lightbox = new MinimogLibs.PhotoSwipeLightbox({
        pswpModule: MinimogLibs.PhotoSwipeLightbox.PhotoSwipe,
        bgOpacity: 1,
        close: false,
        zoom: false,
        arrowNext: false,
        arrowPrev: false,
        counter: false,
        preloader: false,
      });

      const uiElements = [
        {
          name: 'm-close',
          order: 11,
          isButton: true,
          html: '<svg class="m-svg-icon--large" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>',
          onClick: (event, el, pswp) => pswp.close(),
        },
        {
          name: 'm-arrowNext',
          order: 12,
          isButton: true,
          html: '<svg fill="currentColor" width="14px" height="14px" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M218.101 38.101L198.302 57.9c-4.686 4.686-4.686 12.284 0 16.971L353.432 230H12c-6.627 0-12 5.373-12 12v28c0 6.627 5.373 12 12 12h341.432l-155.13 155.13c-4.686 4.686-4.686 12.284 0 16.971l19.799 19.799c4.686 4.686 12.284 4.686 16.971 0l209.414-209.414c4.686-4.686 4.686-12.284 0-16.971L235.071 38.101c-4.686-4.687-12.284-4.687-16.97 0z"></path></svg>',
          onClick: (event, el, pswp) => pswp.next(),
        },
        {
          name: 'm-arrowPrev',
          order: 10,
          isButton: true,
          html: '<svg width="14px" height="14px" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M229.9 473.899l19.799-19.799c4.686-4.686 4.686-12.284 0-16.971L94.569 282H436c6.627 0 12-5.373 12-12v-28c0-6.627-5.373-12-12-12H94.569l155.13-155.13c4.686-4.686 4.686-12.284 0-16.971L229.9 38.101c-4.686-4.686-12.284-4.686-16.971 0L3.515 247.515c-4.686 4.686-4.686 12.284 0 16.971L212.929 473.9c4.686 4.686 12.284 4.686 16.971-.001z"></path></svg>',
          onClick: (event, el, pswp) => pswp.prev(),
        },
      ];

      this.lightbox.on('uiRegister', () => {
        uiElements.forEach((element) => this.lightbox.pswp.ui.registerElement(element));
      });

      this.lightbox.init();

      const openLightbox = (e) => {
        if (e.target.closest('.js-thumb-slider')) return;

        const slide = e.target.closest('.swiper-slide');
        const zoomBtn = e.target.closest('.m-product-media__zoom-in');
        if ((slide && slide.dataset.type === 'image') || zoomBtn) {
          const index = Number(zoomBtn ? zoomBtn.closest('.swiper-slide').dataset.index : slide.dataset.index) || 0;
          this._openPhotoSwipe(index);
        }
      };

      const mainWrapper = this.productWrapper.querySelector(SELECTORS.mainSlider);
      const mobileWrapper = this.productWrapper.querySelector(SELECTORS.mobileSlider);

      if (mainWrapper) {
        const p = mainWrapper.closest('.custom-gallery');
        if (p) p.addEventListener('click', openLightbox);
      }
      if (mobileWrapper) {
        const p = mobileWrapper.closest('.custom-gallery');
        if (p) p.addEventListener('click', openLightbox);
      }

      this.lightbox.on('change', () => {
        if (window.pauseAllMedia) window.pauseAllMedia(this.productWrapper);
        if (this.mainSwiper && this.lightbox.pswp) {
          this.mainSwiper.slideTo(this.lightbox.pswp.currIndex, 100, false);
        }
        if (this.mobileSwiper && this.lightbox.pswp) {
          this.mobileSwiper.slideTo(this.lightbox.pswp.currIndex, 100, false);
        }
      });
    }

    _openPhotoSwipe(index) {
      if (!this.lightbox) return;

      const dataSource = [];
      let allSlides = [];
      if (this.mainSwiper && this.mainSwiper.slides.length > 0) {
        allSlides = this.mainSwiper.slides;
      } else if (this.mobileSwiper && this.mobileSwiper.slides.length > 0) {
        allSlides = this.mobileSwiper.slides;
      } else {
        const wrapper = this.productWrapper.querySelector(SELECTORS.mainSlider) || this.productWrapper.querySelector(SELECTORS.mobileSlider);
        if (wrapper) allSlides = wrapper.querySelectorAll('.swiper-slide');
      }

      Array.from(allSlides).forEach((s) => {
        if (s.dataset.type === 'image') {
          const img = s.querySelector('img');
          if (img) {
            dataSource.push({
              src: img.src,
              width: Math.max(img.naturalWidth, 1600),
              height: Math.max(img.naturalHeight, 1600),
              alt: img.alt || ''
            });
          }
        } else {
          dataSource.push({ html: '<div style="display:flex;align-items:center;justify-content:center;height:100%;color:white;font-size:16px;">Video not supported in zoom view</div>', type: 'html' });
        }
      });

      this.lightbox.options.dataSource = dataSource;
      this.lightbox.loadAndOpen(index);
    }

    /* ────────────────────────────────────────────────── */
    /*  Event binding                                     */
    /* ────────────────────────────────────────────────── */
    _bindEvents() {
      this.productWrapper.addEventListener('change', (e) => {
        const input = e.target;
        
        let configOption = input.dataset ? input.dataset.configOption : null;
        if (!configOption) {
          const nameClean = (input.name || '').replace('options[', '').replace(']', '');
          if (nameClean === 'Metal') {
            configOption = 'metal';
          } else if (nameClean === 'Stone Shape' || nameClean === 'Shape') {
            configOption = 'shape';
          }
        }
        
        if (!configOption) return;
        const group = normalizeKey(configOption);

        if (input.type === 'checkbox') {
          this._handleEngraving(input);
          return;
        }

        if (input.tagName === 'SELECT') {
          this.selections[group] = input.value;
        } else {
          this.selections[group] = input.dataset.configValue || input.value;
        }
        this._updateLabel(group, input);

        // Find the matching real variant so the correct Shopify price is used.
        if (['metal', 'shape', 'center_stone', 'quality', 'carat_weight'].includes(group)) {
          this._findAndSetMatchingVariant();
        }

        this.updatePrice();

        if (group === 'metal' || group === 'shape') {
          this.updateMedia();
        }

        this._updateURL();
      });

      // Do NOT let Minimog's own variant:changed events override our base price
      // once we have a matched variant — those events come from option pickers
      // that are unrelated to our configurator selections.
    }

    /* ────────────────────────────────────────────────── */
    /*  Label text update                                 */
    /* ────────────────────────────────────────────────── */
    _updateLabel(group, input) {
      const el = this.container.querySelector(`[data-config-label="${group}"]`);
      if (!el) return;

      if (group === 'center_stone') {
        el.textContent = this._buildCenterStoneLabel();
      } else if (group === 'quality') {
        // If a center stone is already selected, also refresh its enriched label
        el.textContent = input.value || input.dataset.configValue || '';
        const csEl = this.container.querySelector('[data-config-label="center_stone"]');
        if (csEl && this.selections.center_stone) {
          csEl.textContent = this._buildCenterStoneLabel();
        }
      } else {
        el.textContent = input.value || input.dataset.configValue || '';
      }
    }

    /* ────────────────────────────────────────────────── */
    /*  Engraving toggle                                  */
    /* ────────────────────────────────────────────────── */
    _handleEngraving(checkbox) {
      const wrapper = this.container.querySelector(SELECTORS.engravingInput);
      if (!wrapper) return;
      const textInput = wrapper.querySelector('input[type="text"]');
      if (checkbox.checked) {
        wrapper.classList.add('is-active');
        if (textInput) textInput.disabled = false;
      } else {
        wrapper.classList.remove('is-active');
        if (textInput) { textInput.disabled = true; textInput.value = ''; }
      }
    }

    /* ────────────────────────────────────────────────── */
    /*  Pricing                                            */
    /* ────────────────────────────────────────────────── */
    calculateTotalPrice() {
      // 1. Determine the starting base price
      let total = this._matchedVariantPrice || this._getVariantBasePrice(true);
      
      const matchedVariant = this._matchedVariantId ? (this._allVariants || []).find(v => v.id === this._matchedVariantId) : null;
      const matchedOptions = matchedVariant ? [
        (matchedVariant.option1 || '').trim().toLowerCase(),
        (matchedVariant.option2 || '').trim().toLowerCase(),
        (matchedVariant.option3 || '').trim().toLowerCase()
      ] : [];

      ['metal', 'shape', 'center_stone', 'carat_weight'].forEach((group) => {
        const val = this.selections[group];
        if (!val) return;

        // Skip pricing if this selection is already accounted for in the matched variant options
        if (matchedOptions.includes(val.trim().toLowerCase())) return;

        const cfg = this.config[group];
        if (!cfg || !cfg.pricing) return;
        const delta = cfg.pricing[val];
        if (delta && !isNaN(delta)) total += delta * 100;
      });

      // Quality delta only applies when a center stone has been selected (for per-carat logic)
      // And ONLY if the quality selection is NOT already included in the matched variant
      const qVal = (this.selections.quality || '').trim();
      const qualityInVariant = qVal && matchedOptions.includes(qVal.toLowerCase());

      const stoneSelected = !!this.selections.center_stone;
      if (!qualityInVariant && stoneSelected) {
        const qCfg = this.config.quality;
        if (qCfg && qCfg.pricing) {
          const qVal = this.selections.quality;
          if (qVal) {
            const rawDelta = qCfg.pricing[qVal];

            if (rawDelta !== undefined && !isNaN(rawDelta)) {
              // ── Flat quality pricing (same for all carats) ──
              total += rawDelta * 100;
            } else {
              // ── Per-carat quality pricing (nested by center_stone weight) ──
              // Structure: pricing: { "1ct": { "F-VS2": 0, ... }, "5ct": { ... } }
              const caratVal = this.selections.center_stone; // e.g. "1ct"
              if (caratVal) {
                let tierDelta = null;

                // 1. Exact match for the selected carat
                if (qCfg.pricing[caratVal] && qCfg.pricing[caratVal][qVal] !== undefined) {
                  tierDelta = qCfg.pricing[caratVal][qVal];
                } else {
                  // 2. Find the closest lower carat tier
                  const caratNum = parseFloat(caratVal); // "2ct" → 2
                  let bestTier = null;
                  let bestNum = -1;
                  Object.keys(qCfg.pricing).forEach((tierKey) => {
                    // Only consider keys that are carat tiers (objects, not quality names)
                    if (typeof qCfg.pricing[tierKey] !== 'object') return;
                    const tierNum = parseFloat(tierKey);
                    if (!isNaN(tierNum) && tierNum <= caratNum && tierNum > bestNum) {
                      bestNum = tierNum;
                      bestTier = tierKey;
                    }
                  });
                  if (bestTier && qCfg.pricing[bestTier][qVal] !== undefined) {
                    tierDelta = qCfg.pricing[bestTier][qVal];
                  }
                }

                if (tierDelta !== null && !isNaN(tierDelta)) {
                  total += tierDelta * 100;
                }
              }
            }
          }
        }
      }

      return total;
    }

    updatePrice() {
      const total = this.calculateTotalPrice();
      const fmt = window.MinimogSettings ? window.MinimogSettings.money_format : '${{amount}}';
      const pw = this.productWrapper.querySelector(SELECTORS.priceWrapper);
      if (!pw) return;

      const formatted = this._formatMoney(total, fmt);
      const sale = pw.querySelector(SELECTORS.salePrice);
      if (sale) sale.innerHTML = formatted;
      if (!pw.classList.contains('m-price--on-sale')) {
        pw.querySelectorAll(SELECTORS.regularPrice).forEach((el) => { el.innerHTML = formatted; });
      }
      pw.classList.remove('m:visibility-hidden');
    }

    _formatMoney(cents, format) {
      if (typeof window.formatMoney === 'function') return window.formatMoney(cents, format);
      const amount = (cents / 100).toFixed(2);
      const noDecimals = Math.floor(cents / 100).toString();
      const withCommas = noDecimals.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
      const withCommasDecimals = amount.replace(/(\d)(?=(\d{3})+\.)/g, '$1,');
      let r = format || '${{amount}}';
      return r
        .replace('{{amount_with_comma_separator}}', withCommasDecimals.replace('.', ','))
        .replace('{{amount_no_decimals_with_comma_separator}}', withCommas.replace(',', '.'))
        .replace('{{amount_no_decimals}}', noDecimals)
        .replace('{{amount_with_apostrophe_separator}}', withCommasDecimals.replace(',', "'"))
        .replace('{{amount}}', withCommasDecimals);
    }

    /* ────────────────────────────────────────────────── */
    /*  MEDIA RESOLUTION  (combination > metal > shape)   */
    /* ────────────────────────────────────────────────── */
    _resolveMedia() {
      const media = this.config.media;
      const metal = normalizeKey(this.selections.metal);
      const shape = normalizeKey(this.selections.shape);

      if (media) {
        // Build priority list of keys to look up
        const keysToTry = [];

        // 1. combination  (metal|shape — pipe separator)
        if (metal && shape && media.combination) {
          keysToTry.push({ section: 'combination', key: `${metal}|${shape}` });
          // Also try underscore variant for backward compat
          keysToTry.push({ section: 'combination', key: `${metal}_${shape}` });
        }

        // 2. metal only
        if (metal && media.metal) {
          keysToTry.push({ section: 'metal', key: metal });
        }

        // 3. shape only
        if (shape && media.shape) {
          keysToTry.push({ section: 'shape', key: shape });
        }

        // --- Legacy flat structure fallback (media.{key}) ---
        if (metal && shape) keysToTry.push({ section: null, key: `${metal}_${shape}` });
        if (metal) keysToTry.push({ section: null, key: metal });
        if (shape) keysToTry.push({ section: null, key: shape });

        for (const { section, key } of keysToTry) {
          const bucket = section ? media[section] : media;
          if (bucket && bucket[key]) {
            const entry = bucket[key];
            // Normalize: could be an array directly or { images:[], video:'' }
            if (Array.isArray(entry)) {
              return { key: `${section || 'flat'}_${key}`, items: entry };
            }
            return { key: `${section || 'flat'}_${key}`, items: entry };
          }
        }
      }

      // 4. Fallback: Prioritize the Shopify variant's own featured image if uploaded
      const matchedVariant = this._matchedVariantId ? (this._allVariants || []).find(v => v.id === this._matchedVariantId) : null;
      if (matchedVariant) {
        const img = matchedVariant.featured_image ? (matchedVariant.featured_image.src || matchedVariant.featured_image) : 
                    (matchedVariant.featured_media ? (matchedVariant.featured_media.preview_image ? matchedVariant.featured_media.preview_image.src : null) : null);
        if (img && typeof img === 'string') {
          return {
            key: `variant_${matchedVariant.id}`,
            items: [img]
          };
        }
      }

      // 5. Fallback: first available key in any section
      if (media) {
        for (const sec of ['combination', 'metal', 'shape']) {
          if (media[sec]) {
            const firstKey = Object.keys(media[sec])[0];
            if (firstKey) return { key: `${sec}_${firstKey}`, items: media[sec][firstKey] };
          }
        }

        // Absolute fallback: first key in flat media
        const flatKeys = Object.keys(media).filter(k => !['combination', 'metal', 'shape'].includes(k));
        if (flatKeys.length) return { key: `flat_${flatKeys[0]}`, items: media[flatKeys[0]] };
      }

      return { key: '__default__', items: null };
    }

    /* ────────────────────────────────────────────────── */
    /*  Update media (debounced caching)                  */
    /* ────────────────────────────────────────────────── */
    updateMedia() {
      const { key, items } = this._resolveMedia();

      // Skip if same selection (no DOM thrash)
      if (key === this._lastMediaKey) return;
      this._lastMediaKey = key;

      if (!items) {
        // Show Shopify default (do nothing — gallery stays as-is)
        return;
      }

      // Normalize items into { images:[], video:'' }
      let images, video;
      if (Array.isArray(items)) {
        images = items.filter(i => typeof i === 'string' || (i && i.type === 'image')).map(i => typeof i === 'string' ? i : i.src);
        const vid = items.find(i => i && i.type === 'video');
        video = vid ? vid.src : null;
      } else {
        images = items.images || [];
        video = items.video || null;
      }

      this._renderGallery(images, video);
    }

    /* ────────────────────────────────────────────────── */
    /*  Render gallery (desktop + mobile)                 */
    /* ────────────────────────────────────────────────── */
    _renderGallery(images, video) {
      // Capture current slide index from the VISIBLE swiper (desktop or mobile)
      let savedIndex = 0;
      const desktopGallery = this.productWrapper.querySelector(SELECTORS.desktopGallery);
      const mobileGallery = this.productWrapper.querySelector(SELECTORS.mobileGallery);
      const isDesktop = desktopGallery && desktopGallery.offsetParent !== null;
      const isMobile = mobileGallery && mobileGallery.offsetParent !== null;

      if (isDesktop && this.mainSwiper && typeof this.mainSwiper.activeIndex === 'number') {
        savedIndex = this.mainSwiper.activeIndex;
      } else if (isMobile && this.mobileSwiper && typeof this.mobileSwiper.activeIndex === 'number') {
        savedIndex = this.mobileSwiper.activeIndex;
      }
      // Total slides in the new set (images + optional video)
      const newTotal = images.length + (video ? 1 : 0);
      // Clamp: if user was on slide 3 but new set only has 2 slides, go to last slide
      const restoreIndex = Math.min(savedIndex, newTotal - 1);

      // Add loading state
      const galleries = this.productWrapper.querySelectorAll('.custom-gallery');
      galleries.forEach(g => g.classList.add('is-loading'));

      // Destroy existing swipers
      this._destroySwipers();

      // Build slides HTML
      let mainHtml = '', thumbHtml = '';

      images.forEach((src, i) => {
        mainHtml += `<div class="swiper-slide skeleton-loading" data-index="${i}" data-type="image"><img src="${src}" loading="${i === restoreIndex ? 'eager' : 'lazy'}" alt="Product image ${i + 1}" draggable="false" onload="this.parentElement.classList.remove('skeleton-loading')" /></div>`;
        thumbHtml += `<div class="swiper-slide skeleton-loading" data-index="${i}" data-type="image"><img src="${src}" loading="lazy" alt="Thumbnail ${i + 1}" draggable="false" onload="this.parentElement.classList.remove('skeleton-loading')" /></div>`;
      });

      if (video) {
        const idx = images.length;
        mainHtml += `<div class="swiper-slide skeleton-loading" data-index="${idx}" data-type="video"><div class="custom-gallery__video-wrap"><video src="${video}" controls playsinline preload="metadata" muted loop onloadeddata="this.parentElement.parentElement.classList.remove('skeleton-loading')"></video><div class="custom-gallery__play-btn"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg></div></div></div>`;
        thumbHtml += `<div class="swiper-slide skeleton-loading" data-index="${idx}" data-type="video"><div class="custom-gallery__thumb-video"><video src="${video}" muted preload="metadata" onloadeddata="this.parentElement.parentElement.classList.remove('skeleton-loading')"></video><div class="custom-gallery__thumb-play"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg></div></div></div>`;
      }

      // Inject into desktop
      const mainWrapper = this.productWrapper.querySelector(SELECTORS.mainSlider);
      const thumbWrapper = this.productWrapper.querySelector(SELECTORS.thumbSlider);
      if (mainWrapper) mainWrapper.innerHTML = mainHtml;
      if (thumbWrapper) thumbWrapper.innerHTML = thumbHtml;

      // Inject into mobile
      const mobileWrapper = this.productWrapper.querySelector(SELECTORS.mobileSlider);
      if (mobileWrapper) mobileWrapper.innerHTML = mainHtml;

      // Inject mobile thumbnail strip (mark the restored index as active)
      const mobileThumbStrip = this.productWrapper.querySelector(SELECTORS.mobileThumbStrip);
      if (mobileThumbStrip) {
        let mobileThumbHtml = '';
        images.forEach((src, i) => {
          mobileThumbHtml += `<div class="mobile-thumb-strip__item${i === restoreIndex ? ' is-active' : ''}" data-thumb-index="${i}" data-type="image"><img src="${src}" loading="lazy" alt="Thumbnail ${i + 1}" draggable="false" /></div>`;
        });
        if (video) {
          const idx = images.length;
          mobileThumbHtml += `<div class="mobile-thumb-strip__item${idx === restoreIndex ? ' is-active' : ''}" data-thumb-index="${idx}" data-type="video"><div class="mobile-thumb-strip__video-wrap"><video src="${video}" muted preload="metadata"></video><div class="mobile-thumb-strip__play-icon"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg></div></div></div>`;
        }
        mobileThumbStrip.innerHTML = mobileThumbHtml;
      }

      // Initialize swipers after DOM paint
      requestAnimationFrame(() => {
        this._initSwipers(restoreIndex);
        // Remove loading state
        galleries.forEach(g => g.classList.remove('is-loading'));
      });
    }

    /* ────────────────────────────────────────────────── */
    /*  Swiper init & destroy                             */
    /* ────────────────────────────────────────────────── */
    _destroySwipers() {
      [this.mainSwiper, this.thumbSwiper, this.mobileSwiper].forEach(s => {
        if (s && typeof s.destroy === 'function') s.destroy(true, true);
      });
      this.mainSwiper = this.thumbSwiper = this.mobileSwiper = null;
    }

    _initSwipers(startIndex = 0) {
      if (typeof Swiper === 'undefined') {
        console.warn('[Configurator] Swiper not loaded');
        return;
      }

      const wrapper = this.productWrapper;

      // ── Thumbnail slider (desktop) ──
      const thumbEl = wrapper.querySelector('.js-thumb-slider');
      if (thumbEl) {
        this.thumbSwiper = new Swiper(thumbEl, {
          initialSlide: startIndex,
          slidesPerView: 4,
          spaceBetween: 10,
          freeMode: true,
          watchSlidesProgress: true,
        });
      }

      // ── Main slider (desktop) ──
      const mainEl = wrapper.querySelector('.js-main-slider');
      if (mainEl) {
        this.mainSwiper = new Swiper(mainEl, {
          initialSlide: startIndex,
          slidesPerView: 1,
          spaceBetween: 0,
          loop: false,
          navigation: {
            nextEl: mainEl.querySelector('.swiper-button-next'),
            prevEl: mainEl.querySelector('.swiper-button-prev'),
          },
          thumbs: {
            swiper: this.thumbSwiper,
          },
          on: {
            slideChange: () => this._handleSlideChange(this.mainSwiper),
          },
        });
      }

      // ── Mobile slider ──
      const mobileEl = wrapper.querySelector('.js-mobile-slider');
      if (mobileEl) {
        this.mobileSwiper = new Swiper(mobileEl, {
          initialSlide: startIndex,
          slidesPerView: 1,
          spaceBetween: 0,
          pagination: {
            el: mobileEl.querySelector('.swiper-pagination'),
            clickable: true,
          },
          on: {
            slideChange: () => {
              this._handleSlideChange(this.mobileSwiper);
              this._syncMobileThumbActive();
            },
          },
        });
      }

      // Thumb click → go to slide (desktop)
      if (this.thumbSwiper && this.mainSwiper) {
        const thumbSlides = wrapper.querySelectorAll('.js-thumb-slider .swiper-slide');
        thumbSlides.forEach((slide, i) => {
          slide.addEventListener('click', () => {
            this.mainSwiper.slideTo(i);
          });
        });
      }

      // Mobile thumb strip click → go to slide
      const mobileThumbStrip = wrapper.querySelector(SELECTORS.mobileThumbStrip);
      if (mobileThumbStrip && this.mobileSwiper) {
        mobileThumbStrip.addEventListener('click', (e) => {
          const thumb = e.target.closest('.mobile-thumb-strip__item');
          if (!thumb) return;
          const idx = Number(thumb.dataset.thumbIndex);
          if (!isNaN(idx)) {
            this.mobileSwiper.slideTo(idx);
          }
        });
      }
    }

    /* ────────────────────────────────────────────────── */
    /*  Video play/pause on slide change                  */
    /* ────────────────────────────────────────────────── */
    _handleSlideChange(swiper) {
      if (!swiper || !swiper.slides) return;
      // Pause all videos
      swiper.slides.forEach((slide) => {
        const vid = slide.querySelector('video');
        if (vid) { vid.pause(); }
        const playBtn = slide.querySelector('.custom-gallery__play-btn');
        if (playBtn) playBtn.style.display = '';
      });
      // Auto-play current video slide
      const current = swiper.slides[swiper.activeIndex];
      if (current && current.dataset.type === 'video') {
        const vid = current.querySelector('video');
        if (vid) {
          vid.play().catch(() => { });
          const playBtn = current.querySelector('.custom-gallery__play-btn');
          if (playBtn) playBtn.style.display = 'none';
        }
      }
    }

    /* ────────────────────────────────────────────────── */
    /*  Sync mobile thumbnail active state               */
    /* ────────────────────────────────────────────────── */
    _syncMobileThumbActive() {
      if (!this.mobileSwiper) return;
      const strip = this.productWrapper.querySelector(SELECTORS.mobileThumbStrip);
      if (!strip) return;
      const items = strip.querySelectorAll('.mobile-thumb-strip__item');
      const activeIdx = this.mobileSwiper.activeIndex;
      items.forEach((item, i) => {
        item.classList.toggle('is-active', i === activeIdx);
      });
      // Scroll active thumb into view
      if (items[activeIdx]) {
        items[activeIdx].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }

    /* ────────────────────────────────────────────────── */
    /*  Quality parser helper                             */
    /* ────────────────────────────────────────────────── */
    _parseQuality(qualVal) {
      // "F-VS2" → { color: 'F', clarity: 'VS2' }
      // "D-VVS2" → { color: 'D', clarity: 'VVS2' }
      const parts = (qualVal || '').split('-');
      return { color: parts[0] || '', clarity: parts[1] || '' };
    }

    /* ────────────────────────────────────────────────── */
    /*  Build enriched center stone label                 */
    /* ────────────────────────────────────────────────── */
    _buildCenterStoneLabel() {
      const carat = this.selections.center_stone; // e.g. "1ct"
      if (!carat) return '';
      const qual = this.selections.quality || '';
      const { color, clarity } = this._parseQuality(qual);
      const parts = [carat];
      if (color) parts.push(`${color} color`);
      if (clarity) parts.push(`${clarity} clarity`);
      parts.push('IGI Certified');
      return parts.join(', ');
    }

    /* ────────────────────────────────────────────────── */
    /*  Intercept Add-to-Cart — pass custom price         */
    /* ────────────────────────────────────────────────── */

    /**
     * Attach a capture-phase submit listener on the product form.
     * Fires BEFORE Minimog's ProductForm.onSubmitHandler so we can
     * cancel the native submission and replace it with a JSON fetch
     * that includes the dynamically-calculated price in cents.
     */
    _interceptFormSubmit() {
      // Find all forms for this product (main form and sticky bar form)
      const forms = document.querySelectorAll(`form[data-type="add-to-cart-form"]`);
      const productForms = Array.from(forms).filter(f => {
        // Match by product ID if available, otherwise assume main product wrapper's form
        const fid = f.dataset.productId || (f.querySelector('[name="product-id"]') || {}).value;
        return fid === this.productId || f.closest(SELECTORS.productWrapper) === this.productWrapper;
      });

      productForms.forEach(form => {
        // capture: true  → fires before Minimog's bubbling-phase handler
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          e.stopImmediatePropagation(); // block Minimog's handler
          this._submitWithCustomPrice(form);
        }, true);
      });
    }

    /**
     * POST to /cart/add.js with a price override so Shopify stores
     * the calculated total on the line item, not just the variant price.
     */
    async _submitWithCustomPrice(form) {
      if (!form) form = this.productWrapper.querySelector('form[data-type="add-to-cart-form"]');
      if (!form) return;

      // ── Validate ring size if present ──────────────────
      // Always look in the main configurator for the ring size
      const ringSelect = this.container.querySelector('select[name="properties[Ring Size]"]');
      if (ringSelect && ringSelect.required && !ringSelect.value) {
        ringSelect.focus();
        // Scroll to it if it's hidden or far away (e.g. if we submitted from sticky)
        ringSelect.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        if (window.MinimogTheme && window.MinimogTheme.Notification) {
          const errWrap = this.productWrapper.querySelector('.m-product-form-message');
          window.MinimogTheme.Notification.show({
            target: errWrap || document.body,
            method: 'appendChild',
            type: 'warning',
            message: window.MinimogStrings ? window.MinimogStrings.requiredField : 'Please select a ring size.',
            last: 3000,
            sticky: !errWrap,
          });
        }
        return;
      }

      // ── Gather variant id & quantity ───────────────────
      // Prefer the ID from the form that was actually submitted (handles sticky dropdowns)
      const variantInput = form.querySelector('input[name="id"]') || form.querySelector('select[name="id"]');
      if (!variantInput) return;
      const variantId = Number(variantInput.value);
      if (!variantId) return;

      const qtyInput = form.querySelector('[name="quantity"]');
      const quantity = Number(qtyInput ? qtyInput.value : 1) || 1;

      // ── Find the correct Shopify variant (Center Stone × Quality) ──────────
      // If a match was already found when the user picked options, use it.
      // Otherwise do a fresh lookup now.
      let targetVariantId = this._matchedVariantId || variantId;
      const freshMatch = this._findAndSetMatchingVariant();
      if (freshMatch) targetVariantId = freshMatch.id;

      // ── Collect line item properties ───────────────────────────────────────
      // Metal and Shape are passed as order-visible properties.
      // Center Stone and Quality drive the variant choice, but we still include
      // them as properties so they appear in the order summary.
      const properties = {};

      // 1. Pull every input explicitly named as a property
      this.productWrapper.querySelectorAll('[name^="properties["]').forEach((input) => {
        if (input.disabled) return;
        if (input.type === 'checkbox' && !input.checked) return;
        if (input.type === 'radio' && !input.checked) return;
        const key = input.name.replace('properties[', '').replace(']', '');
        if (key && input.value && !key.startsWith('_')) properties[key] = input.value;
      });

      // 2. Explicitly add all selections from our configurator state
      // This ensures that even if inputs are missing from the specific form submitted
      // (like the sticky bar), the user's choices are still preserved.
      if (this.selections.metal)         properties['Metal']         = this.selections.metal;
      if (this.selections.shape)         properties['Shape']         = this.selections.shape;
      if (this.selections.center_stone)  properties['Center Stone']  = this.selections.center_stone;
      if (this.selections.carat_weight)  properties['Carat Weight']  = this.selections.carat_weight;
      if (this.selections.quality)       properties['Quality']       = this.selections.quality;
      if (this.selections.ring_size)     properties['Ring Size']     = this.selections.ring_size;

      // ── Single-item payload — no dummy product needed ───────────────────────
      const itemsPayload = [{
        id: targetVariantId,
        quantity: quantity,
        properties: properties
      }];

      // ── Locate Minimog cart elements ───────────────────
      const cartDrawerEl = document.querySelector('m-cart-drawer');
      const cartPageEl   = document.querySelector('m-cart');
      const productFormEl = this.productWrapper.querySelector('product-form');

      // ── Show spinner via Minimog's ProductForm element ─
      if (productFormEl && typeof productFormEl.toggleSpinner === 'function') {
        productFormEl.toggleSpinner(true);
      }

      // ── Build sections list for cart drawer re-render ──
      const sections = cartDrawerEl
        ? cartDrawerEl.getSectionsToRender().map((s) => s.id)
        : [];

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items: itemsPayload,
            sections,
            sections_url: window.location.pathname,
          }),
        });

        const response = await res.json();

        // ── Error from Shopify ──────────────────────────
        if (response.status && response.status >= 400) {
          if (window.MinimogEvents && window.MinimogTheme) {
            window.MinimogEvents.emit(MinimogTheme.pubSubEvents.cartError, {
              source: 'product-configurator',
              productVariantId: targetVariantId,
              errors: response.description,
              message: response.message,
            });
            const errWrap = this.productWrapper.querySelector('.m-product-form-message');
            window.MinimogTheme.Notification.show({
              target: errWrap || document.body,
              method: 'appendChild',
              type: 'error',
              message: response.message || 'Could not add to cart.',
              last: 3000,
              sticky: !errWrap,
            });
          }
          return;
        }

        // ── Success ─────────────────────────────────────
        const { MinimogSettings, MinimogStrings } = window;

        if (MinimogSettings && MinimogSettings.use_ajax_atc) {
          if (cartDrawerEl && MinimogSettings.enable_cart_drawer) {
            cartDrawerEl.renderContents(response);
            if (window.MinimogTheme && window.MinimogTheme.Notification) {
              window.MinimogTheme.Notification.show({
                target: cartDrawerEl.querySelector('m-cart-drawer-items'),
                method: 'prepend',
                type: 'success',
                message: MinimogStrings ? MinimogStrings.itemAdded : 'Item added!',
                delay: 400,
              });
            }
            window.MinimogEvents.emit(MinimogTheme.pubSubEvents.openCartDrawer);
          } else {
            if (window.MinimogTheme && window.MinimogTheme.Notification) {
              const errWrap = this.productWrapper.querySelector('.m-product-form-message');
              window.MinimogTheme.Notification.show({
                target: errWrap || document.body,
                method: 'appendChild',
                type: 'success',
                message: MinimogStrings ? MinimogStrings.itemAdded : 'Item added!',
                last: 3000,
                sticky: !errWrap,
              });
            }
            fetch(window.Shopify.routes.root + 'cart.json')
              .then((r) => r.json())
              .then((cart) => {
                const count = cart.item_count;
                document.querySelectorAll('.m-cart-count-bubble').forEach((el) => {
                  el.textContent = count;
                  el.classList.toggle('m:hidden', count === 0);
                });
              }).catch(() => {});
          }

          document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: response } }));
          window.MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, response);

          if (cartDrawerEl) {
            cartDrawerEl.classList.remove('m-cart--empty');
            const drawerItems = cartDrawerEl.querySelector('m-cart-drawer-items');
            if (drawerItems) drawerItems.classList.remove('m-cart--empty');
          }
          if (cartPageEl) cartPageEl.classList.remove('m-cart--empty');

        } else {
          window.location = MinimogSettings ? MinimogSettings.routes.cart : '/cart';
        }

      } catch (err) {
        console.error('[Configurator] Add to cart failed:', err);
        if (window.MinimogTheme && window.MinimogTheme.Notification) {
          const errWrap = this.productWrapper.querySelector('.m-product-form-message');
          window.MinimogTheme.Notification.show({
            target: errWrap || document.body,
            method: 'appendChild',
            type: 'error',
            message: 'Could not add to cart. Please try again.',
            last: 3000,
            sticky: !errWrap,
          });
        }
      } finally {
        if (productFormEl && typeof productFormEl.toggleSpinner === 'function') {
          productFormEl.toggleSpinner(false);
        }
      }
    }

    /* ────────────────────────────────────────────────── */
    /*  URL params                                        */
    /* ────────────────────────────────────────────────── */
    _updateURL() {
      const url = new URL(window.location.href);
      Object.entries(this.selections).forEach(([key, value]) => {
        if (value) url.searchParams.set(key, value);
      });
      window.history.replaceState({}, '', url.toString());
    }
  }

  /* ─── Bootstrap ────────────────────────────────────── */
  function initConfigurators() {
    document.querySelectorAll(SELECTORS.configurator).forEach((el) => {
      if (!el._configuratorInit) {
        el._configuratorInit = true;
        new ProductConfigurator(el);
      }
    });

    // Bind video play-button overlays (event delegation)
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.custom-gallery__play-btn');
      if (!btn) return;
      const wrap = btn.closest('.custom-gallery__video-wrap');
      if (!wrap) return;
      const vid = wrap.querySelector('video');
      if (vid) { vid.play().catch(() => { }); btn.style.display = 'none'; }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initConfigurators);
  } else {
    initConfigurators();
  }

  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', initConfigurators);
  }
})();
